/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/main.tsx
 * PURPOSE: Vietnamese F&B PWA application entry point with cultural intelligence and offline-first architecture
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P5 (SASE Integration - AI/ML) + P1 (Stage 06)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Frontend Application, Vietnamese F&B Platform
 * - Impact: Application Quality, User Experience, Performance
 * - Stakeholders: Development Team, End Users, Product Team
 *
 * @technical-architecture
 * - Layer: Frontend Application Layer
 * - Pattern: React Application, TypeScript, Modern Frontend
 * - Dependencies: React 18, TypeScript, Vite, TailwindCSS
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App"; // Full NQH-Bot application with login and dashboard
// import TestApp from "./TestApp"; // Simple demo app for testing
// import SimpleApp from "./SimpleApp"; // Temporary basic test component
// import ProfessionalLogin from "./ProfessionalLogin"; // Professional glassmorphism login design
import "./index.css";
import "./styles/design-system.css"; // ✅ ENABLED: NQH Brand Design System V2.0 (SDLC 6.3.1 Mandate)
import "./styles/nqh-design-tokens.css"; // ✅ Sprint 17 P0 Fix: NQH Design Tokens for CSS Modules
import "./styles/animations.css"; // ✅ Sprint 33: NQH Animation System (30+ keyframes, Vietnamese identity)
import "./i18n/config"; // Initialize i18n (required for VI/EN toggle)

// BUG-008 P0 Fix: QueryClient instance for TanStack Query v5
// Without this, all useQuery/useMutation calls throw "No QueryClient set" → blank page
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000, // 30s stale time to reduce redundant fetches
    },
  },
});

// ISSUE-027: removed manual register('/sw.js') — VitePWA registerType:'autoUpdate'
// virtual module handles SW registration. Dual registration caused confusion about
// which SW implementation was active (public/sw.js vs VitePWA-generated Workbox SW).

// 2026-08-30: tự động gỡ Service Worker cũ khi chạy DEV — bug tái diễn ≥4 lần
// (ghi nhận 2026-08-27 → 2026-08-30, xem mobile/docs/DEVICE-CONNECTION-GUIDE.md
// §4.3/§4.3.1): `vite dev` KHÔNG tự đăng ký SW, nhưng nếu trình duyệt từng mở
// bản production trên CÙNG origin (localhost:2500, qua nginx/docker hoặc
// `vite build` + serve tĩnh) thì SW đó vẫn còn active — `public/sw.js` dùng
// chiến lược cache-first cho mọi request không phải /api/, nên phục vụ lại
// HTML/JS/CSS đã cache TỪ LÚC ĐĂNG KÝ, độc lập hoàn toàn với việc dev server
// phía sau có build lại code mới hay không → hiện tượng "lúc mới lúc cũ" người
// dùng báo lại nhiều lần, mỗi lần phải tự F12 → Unregister → Clear site data
// bằng tay. Tự động hoá bước đó — CHỈ chạy khi `import.meta.env.DEV`, không
// đụng gì tới hành vi PWA autoUpdate thật ở production.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    if (registrations.length === 0) return;
    console.warn(
      `[dev] Gỡ ${registrations.length} Service Worker cũ (có thể còn sót từ lần mở bản production trước) ` +
        "để tránh cache-first phục vụ nhầm bản cũ trong lúc dev — xem DEVICE-CONNECTION-GUIDE.md §4.3.1"
    );
    registrations.forEach((registration) => registration.unregister());
  });
  // Cache Storage (không chỉ SW registration) — cache-first trong public/sw.js
  // đọc thẳng từ đây, gỡ SW không tự xoá cache cũ đã lưu.
  if ("caches" in window) {
    caches.keys().then((keys) => keys.forEach((key) => caches.delete(key)));
  }
}

// S95L-016: Self-hosted Web Push (VAPID) — no Firebase dependency
// Requires: VITE_VAPID_PUBLIC_KEY (generated by backend pywebpush)
// iOS Safari: isFCMSupported() returns false → in-app notifications only
if (import.meta.env.PROD && import.meta.env.VITE_VAPID_PUBLIC_KEY) {
  window.addEventListener("load", async () => {
    const { isFCMSupported, initFCM } = await import("./services/firebase");
    if (!isFCMSupported()) {
      console.info("[Push] Not supported on this platform — in-app notifications active");
      return;
    }
    const endpoint = await initFCM();
    if (endpoint) console.info("[Push] Web Push enabled, subscription registered");
  });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
